import React from 'react'
import { Box, FormControlLabel, Switch, Typography } from '@material-ui/core'
import { Muted } from './ui'
import { effectiveLongRead } from 'src/services/analysisStart'
import { LONG_READ_TOOLS } from '../model/workflow'
import { setLongReadOverride } from '../model/samples'

// CNV, SV and Methylation for ONT and PacBio, each with the tool that runs it.
//
//   <LongReadOptions workflow onChange stats />          the batch (column) switches
//   <LongReadOptions workflow sample onChange />         one sample's own options
//
// onChange always receives a patch: a workflow patch ({ lrCnv }) for the batch, or a
// sample patch ({ longRead }) from setLongReadOverride for a sample. A sample's
// switches start from its effective values; PacBio methylation needs a file with
// methylation data, ONT methylation is always switchable, as in the wizard.

const FIELDS = [
  { field: 'cnv', label: 'CNV', batchField: 'lrCnv' },
  { field: 'sv', label: 'SV', batchField: 'lrSv' },
  { field: 'meth', label: 'Methylation', batchField: 'lrMeth' },
]

/** The PacBio methylation line under the batch switch, from { total, methylationReady }. */
export const methylationHint = (stats) => {
  const total = Number(stats?.total) || 0
  const ready = Math.min(Number(stats?.methylationReady) || 0, total)
  if (!total) return 'Runs on files with methylation data.'
  if (ready === total)
    return total === 1 ? 'This file has methylation data.' : `All ${total} files have methylation data.`
  if (ready === 0) return total === 1 ? 'No methylation data in this file.' : 'No file has methylation data.'
  return `Runs on ${ready} of ${total} files with methylation data.`
}

const SwitchRow = ({ label, tool, checked, onChange, disabled, hint, ariaLabel }) => (
  <Box>
    <FormControlLabel
      control={
        <Switch
          size="small"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          inputProps={{ 'aria-label': ariaLabel }}
        />
      }
      disabled={disabled}
      label={
        <Typography component="span" sx={{ fontSize: 14.5 }}>
          {label}
          {tool && (
            <Typography component="span" sx={{ fontSize: 13, color: 'text.secondary', ml: 1 }}>
              {tool}
            </Typography>
          )}
        </Typography>
      }
      sx={{ ml: 0, gap: 1, '& .MuiSwitch-root': { ml: -0.5 } }}
    />
    {hint && <Muted sx={{ ml: 5.25, mt: -0.25 }}>{hint}</Muted>}
  </Box>
)

const LongReadOptions = ({ workflow, sample, onChange, stats }) => {
  const tools = LONG_READ_TOOLS[workflow.input] || {}
  const pacbio = workflow.input === 'pacbio'
  const values = sample
    ? effectiveLongRead(workflow, sample)
    : { cnv: !!workflow.lrCnv, sv: !!workflow.lrSv, meth: !!workflow.lrMeth }
  const noMethData = Boolean(sample && pacbio && !sample.methylationReady)

  const toggle =
    ({ field, batchField }) =>
    (value) => {
      if (sample) onChange(setLongReadOverride(sample, field, value, workflow))
      else onChange({ [batchField]: value })
    }

  return (
    <Box>
      {FIELDS.map((f) => {
        let hint
        if (f.field === 'meth' && pacbio)
          hint = noMethData ? 'No methylation data' : sample ? '' : methylationHint(stats)
        return (
          <SwitchRow
            key={f.field}
            label={f.label}
            tool={tools[f.field]}
            checked={!!values[f.field]}
            onChange={toggle(f)}
            disabled={f.field === 'meth' && noMethData}
            hint={hint}
            ariaLabel={sample ? `${f.label} for ${sample.key}` : f.label}
          />
        )
      })}
    </Box>
  )
}

export default LongReadOptions
